import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { companyHasPlanFeature } from '../../components/plan-gate.tsx';
import { LoadingBlock } from '../../components/ui.tsx';
import ServiceTemplatesSection from '../../components/service-templates-section.tsx';
import RoomTemplatesSection from '../../components/room-templates-section.tsx';
import GroupTemplatesSection from '../../components/group-templates-section.tsx';
import { SettingsLeaf } from './settings-leaf.tsx';

const FAMILIES = ['services', 'rooms', 'groups'] as const;
type TemplateFamily = (typeof FAMILIES)[number];

function isFamily(value: string): value is TemplateFamily {
  return (FAMILIES as readonly string[]).includes(value);
}

/**
 * Settings → Templates: the service (task), room and group (layout) template
 * managers behind one entry, the way the mobile app has one Templates screen.
 *
 * Each family is a child route rather than a tab of one page: the managers
 * are heavy, and only the one on screen mounts, so opening Rooms does not
 * also load and render the group editor. The families stay distinct — three
 * models, three managers, unchanged inside — and every write inside still
 * asks the permission it asked on /settings. `/settings/templates` is
 * Services, the family most companies open first.
 *
 * Property checklists are a different thing (a list per home, not a quote
 * template) and keep their own page; the footer points there.
 */
export default function TemplatesHubPage() {
  const { t } = useTranslation();
  const { family = 'services' } = useParams<{ family?: string }>();
  const { company, can } = useSession();

  if (!company) return <LoadingBlock label={t('settings.loadingSettings')} />;
  if (!can('templates:read')) return <Navigate to="/settings" replace />;
  if (!isFamily(family)) return <Navigate to="/settings/templates" replace />;

  const showChecklists = companyHasPlanFeature(company, 'scheduling') && can('customers:read');

  return (
    <SettingsLeaf
      title={t('settings.templatesHub.title')}
      description={t('settings.templatesHub.description')}
      tabsLabel={t('settings.templatesHub.tabsLabel')}
      tabs={FAMILIES.map((each) => ({
        to: `/settings/templates/${each}`,
        label: t(`settings.templatesHub.tabs.${each}`),
        current: each === family,
      }))}
    >
      {family === 'services' ? <ServiceTemplatesSection /> : null}
      {family === 'rooms' ? <RoomTemplatesSection /> : null}
      {family === 'groups' ? <GroupTemplatesSection /> : null}

      {showChecklists ? (
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('settings.templatesHub.checklistsHint')}{' '}
          <Link className="font-semibold text-[color:var(--color-brand)] hover:underline" to="/checklist-templates">
            {t('settings.templatesHub.checklistsLink')}
          </Link>
        </p>
      ) : null}
    </SettingsLeaf>
  );
}
